<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	import { format } from 'svelte-i18n';
	import { beforeUpdate } from 'svelte';
	import {
		get_gen_2_control_component_channel_structure,
		get_local_ip,
		is_gen_2,
		real_id,
		isActuator as isActuatorHelper,
	} from '../../../helpers/interface_helpers';
	import isDimmerWithCalibration from '../../../helpers/is_calibratable_dimmer';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	import {
		calculate_timer,
		clear_device_timer_state,
		convertToSeconds,
	} from '../../../helpers/timer_helpers';
	import { handleDeviceTurn } from 'src/helpers/handle_device_turn';
	import { auth_locked_devices } from 'src/stores/device-runtime/local-auth';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { showCalibrationHasStartedModal } from 'src/stores/ui/calibration-modals';
	import { toast, DeviceSwitch } from '@Shelly/shelly-design-system';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import PinUnlock from '../../PinUnlock.svelte';
	import { doNotShowInitCalibrationModal } from 'src/stores/ui/calibration-modals';
	import { hasControlRights } from 'src/helpers/user_helpers';
	import QuickTimeControl from './QuickTimeControl.svelte';

	let className = '';
	export { className as class };

	export let status,
		device,
		settings,
		isWide = false,
		isInSlot = false,
		forceDisabled = false;

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;

	let isLoading = false;
	let ison = false;
	let localIp = false;
	let showQuickControlModal = false;
	const isGen2 = is_gen_2(device);

	$: hasRights = hasControlRights(device);

	$: isActuator = isActuatorHelper(settings, device);
	$: component = isGen2 ? get_gen_2_control_component_channel_structure(device) : undefined;

	let _isDimmerWithCalibration = isDimmerWithCalibration(device.type);
	let hasTimer = false;
	let timer = { time: undefined, percentage: undefined };
	let timerStarted;
	let cacheTimerStarted;

	let pinLocked;
	let showModal = false;
	let disabled = false;

	$: isButtonDisabled = !status?.online || disabled || !hasRights || isActuator || forceDisabled;

	beforeUpdate(() => {
		localIp = get_local_ip(device.id);

		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

		if (status?.online) {
			if (typeof status?.[device.category.split('s_')[0] + 's'] !== 'undefined') {
				if (device.category === 'emeter') {
					ison = status['relays'][0]?.ison;
					hasTimer = status['relays'][0]?.has_timer;
				} else {
					ison = status[device.category.split('s_')[0] + 's'][device.channel]?.ison;
					hasTimer = status[device.category.split('s_')[0] + 's'][device.channel]?.has_timer;
				}
			} else if (isGen2) {
				ison =
					typeof status?.[component]?.output !== 'undefined'
						? status?.[component]?.output
						: status?.[component]?.on;
				hasTimer = typeof status[component]?.timer_started_at !== 'undefined';
			} else {
				ison = false;
			}

			// Timer display: resolve timer identity (Gen1/Gen2/emeter), then keep remaining time in sync
			timerStarted =
				status?.[component]?.timer_started_at ||
				status?.[device.category + 's']?.[device.channel]?.timer_started ||
				status?.['relays']?.[0]?.timer_started;

			if (typeof cacheTimerStarted === 'undefined') {
				cacheTimerStarted = timerStarted;
			}

			if (timerStarted && hasTimer) {
				const [, t, pct] = calculate_timer(device, status, settings);
				const timerJustStartedOrRestarted = cacheTimerStarted !== timerStarted;
				const timerDisplayEmpty = Object.values(timer).some((t) => typeof t === 'undefined');

				if (timerDisplayEmpty || timerJustStartedOrRestarted) {
					if (timerJustStartedOrRestarted) cacheTimerStarted = timerStarted;
					timer.time = t;
					timer.percentage = Number(pct.toFixed(0));
				}
			} else {
				timer.time = undefined;
				timer.percentage = undefined;
			}
			if (device.category === 'breaker') {
				disabled = status?.[get_gen_2_control_component_channel_structure(device)]?.safety;
			}
		}
	});

	/**
	 * Retrieves the actual device status from the device status object.
	 * Supports Gen1, Gen2, and emeter device categories.
	 *
	 * @returns {boolean | undefined} The device status as boolean (true/false) or undefined if unavailable.
	 *                                Converts numeric values (1/0) to boolean (true/false).
	 */
	const getActualDeviceStatus = (): boolean | undefined => {
		if (!status?.online) return undefined;

		let deviceStatus: boolean | number | undefined;

		if (typeof status?.[device.category.split('s_')[0] + 's'] !== 'undefined') {
			if (device.category === 'emeter') {
				deviceStatus = status['relays'][0]?.ison;
			} else {
				deviceStatus = status[device.category.split('s_')[0] + 's'][device.channel]?.ison;
			}
		} else if (isGen2) {
			deviceStatus =
				typeof status?.[component]?.output !== 'undefined'
					? status?.[component]?.output
					: status?.[component]?.on;
		} else {
			return undefined;
		}

		if (deviceStatus === true || deviceStatus === 1) {
			return true;
		}
		if (deviceStatus === false || deviceStatus === 0) {
			return false;
		}
		return undefined;
	};

	/**
	 * Handles device turn on/off operation.
	 * Checks PIN lock, handles calibration modal for dimmers, sends turn request,
	 * and updates device status based on actual device state after successful request.
	 *
	 * @param {number | undefined} flipbackSeconds - Optional seconds to automatically turn device back after.
	 * @returns {Promise<void>}
	 */
	const handleTurn = async (flipbackSeconds: number = undefined) => {
		if (pinLocked) {
			showModal = true;
			return;
		}

		if (isLoading) return;
		isLoading = true;

		try {
			if (
				_isDimmerWithCalibration &&
				status?.[`light:${device.channel}`]?.flags?.includes('uncalibrated')
			) {
				doNotShowInitCalibrationModal.set(false);
				showCalibrationHasStartedModal.update((state) => ({
					...state,
					channel: device.channel,
				}));
			}

			const result = await handleDeviceTurn({ device }, flipbackSeconds);

			if (result.success) {
				const actualStatus = getActualDeviceStatus();
				const newIson = actualStatus !== undefined ? actualStatus : result.expected_ison;

				ison = newIson;

				const nextStatus = { ...status };
				if (isGen2) {
					nextStatus[component] =
						typeof status?.[component]?.output !== 'undefined'
							? { ...status[component], output: newIson }
							: { ...status[component], on: newIson };
				} else if (device.category === 'emeter') {
					nextStatus['relays'] = status['relays'].map((r, i) =>
						i === 0 ? { ...r, ison: newIson } : r,
					);
				} else {
					const key = device.category.split('s_')[0] + 's';
					const arr = [...status[key]];
					arr[device.channel] = { ...arr[device.channel], ison: newIson };
					nextStatus[key] = arr;
				}

				// FE-1790: a manual turn (no `toggle_after`) cancels any running countdown on
				// the device; mirror it in the local cache so the UI timer disappears at once.
				if (!flipbackSeconds) {
					clear_device_timer_state(nextStatus, device);
					hasTimer = false;
					timerStarted = undefined;
					cacheTimerStarted = undefined;
					timer = { time: undefined, percentage: undefined };
				}

				devices_status.update_device_status({
					id: real_id(device.id),
					msg: nextStatus,
				});
			} else if ($auth_locked_devices.indexOf(real_id(device.id)) == -1) {
				toast.open({ type: 'danger', title: $format('error_global') });
			}
		} finally {
			isLoading = false;
		}
	};

	/**
	 * Handles quick time control entry selection.
	 * Converts the selected time entry to seconds and triggers device turn with timer.
	 *
	 * @param {Object} entry - The selected time entry with min and sec properties.
	 * @returns {void}
	 */
	const handleEntryClick = (entry) => {
		const flipbackSeconds = convertToSeconds('00', entry.min, entry.sec);
		handleTurn(flipbackSeconds);
		showQuickControlModal = false;
	};

	/**
	 * Handles PIN unlock and proceeds with device turn operation.
	 * Unlocks the device PIN and immediately triggers the turn operation.
	 *
	 * @returns {void}
	 */
	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleTurn();
	};

	/**
	 * Handles click event on device switch.
	 * Prevents default behavior, checks PIN lock, and triggers device turn.
	 *
	 * @param {Event} e - The click event
	 * @returns {void}
	 */
	const handleClick = (e: Event) => {
		e.stopPropagation();
		e.preventDefault();
		if (pinLocked) {
			showModal = true;
			return;
		}
		handleTurn();
	};

	const a11yProps = {
		timerAriaLabel: $format('label_turn_device', { default: 'Turn device' }),
	};
</script>

{#key timerStarted || hasTimer}
	{#if isWide || isInSlot}
		<div class="d-flex flex-direction-column align-items-center justify-content-center height-full">
			<DeviceSwitch
				id="devicebutton-{device.id}"
				value={ison
					? capFirstChar($format('on', { default: 'on' }))
					: capFirstChar($format('off', { default: 'off' }))}
				aria-label={$format('label_turn_device', { default: 'Turn device' })}
				{a11yProps}
				{isLoading}
				{hasTimer}
				{timer}
				checked={ison}
				variant="primary"
				on:click={handleClick}
				disabled={isButtonDisabled}
				class={className}
			/>
		</div>
	{:else}
		<DeviceSwitch
			id="devicebutton-{device.id}"
			value={ison
				? capFirstChar($format('on', { default: 'on' }))
				: capFirstChar($format('off', { default: 'off' }))}
			aria-label={$format('label_turn_device', { default: 'Turn device' })}
			{a11yProps}
			{isLoading}
			{hasTimer}
			{timer}
			checked={ison}
			isActive={ison}
			on:click={handleClick}
			disabled={isButtonDisabled}
			class={className}
		/>
	{/if}
{/key}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} />
{/if}

<QuickTimeControl
	isOn={ison}
	icon="far fa-clock"
	bind:showQuickControlModal
	on:select={(e) => handleEntryClick(e.detail)}
/>
